import { Alert, Group, Paper, Stack, Text } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import type { Lens } from './lenses';
import OverviewPanel from './OverviewPanel';
import type { UsageModel } from './usageModel';

type Props = {
  model: UsageModel;
  headline: React.ReactNode;
  month: string;
  lens: Lens;
  today: Date;
  onSelectFirm: (name: string) => void;
};

const USAGE_DEFINITION =
  'Actions by customers (ABC) = SR creates + meeting notes + agendas + meetings recorded + files uploaded + ' +
  'Siesta messages, shown both excluding and including new clients added. Rankings, leaderboards and ' +
  'attention flags use ABC excluding new clients, because a single bulk import can add hundreds of clients ' +
  'in a month and would swamp them. Conversations are not added on top of Siesta messages, because a ' +
  'conversation is a thread of messages already counted.';

function MonthInProgress({
  label,
  abc,
  newClients,
  activeFirms,
}: {
  label: string;
  abc: number;
  newClients: number;
  activeFirms: number;
}) {
  return (
    <Alert icon={<IconInfoCircle />} color="orange" variant="light">
      <Group gap="xl" wrap="wrap">
        <Text size="textSm" fw={FONT_WEIGHTS.semibold}>
          {label} so far
        </Text>
        <Text size="textSm">ABC {abc.toLocaleString('en-GB')}</Text>
        <Text size="textSm">New clients added {newClients.toLocaleString('en-GB')}</Text>
        <Text size="textSm">Active firms {activeFirms.toLocaleString('en-GB')}</Text>
        <Text size="textXs" c="dimmed">
          Month in progress — not comparable with a full month
        </Text>
      </Group>
    </Alert>
  );
}

export default function UsageDashboard({
  model,
  headline,
  month,
  lens,
  today,
  onSelectFirm,
}: Props) {
  const partial = model.partialMonth;
  const partialCounts = partial ? model.platform.get(partial) : undefined;

  return (
    <Stack gap="md">
      {partial && partialCounts && (
        <MonthInProgress
          label={model.monthLabels.get(partial) ?? partial}
          abc={partialCounts.abc}
          newClients={partialCounts.newClients}
          activeFirms={model.activeFirms.get(partial) ?? 0}
        />
      )}

      {headline}

      <OverviewPanel
        model={model}
        month={month}
        lens={lens}
        today={today}
        onSelectFirm={onSelectFirm}
      />

      <Paper p="sm" withBorder radius="md">
        <Text size="textXs" c="dimmed">
          {USAGE_DEFINITION}
        </Text>
      </Paper>
    </Stack>
  );
}
